import { fontShorthand } from '../core/paint/text';

/**
 * The two alignment rules that keep every text surface on one baseline.
 *
 * The model's rule (`core/paint/text.ts`) is the convention: the first baseline
 * sits at `lineHeight / 2` below the box top, and consecutive baselines are
 * `fontSize × lineHeight` apart. The bake (`io/textRaster`), the export and the
 * merges already draw exactly that. The other two surfaces need help:
 *
 *  - **The live scene** draws through Fabric, whose internal text maths is not
 *    metric-derived at all: every line box is `_fontSizeMult` (1.13×) the font
 *    size tall regardless of line height, the alphabetic baseline is drawn
 *    `1 − _fontSizeFraction` (77.8%) into that box, and the line pitch is
 *    therefore 1.13 × lineHeight × fontSize. `fabricTextAlignment` compensates
 *    both, so the scene lands on the model's baselines instead of the model
 *    adopting Fabric's arbitrary constants.
 *  - **The editor overlay** is a DOM textarea, whose first baseline is wherever
 *    CSS half-leading puts it: `(lineHeight − ascent − descent) / 2 + ascent`
 *    below the content top. `domBaselineOffset` says how far to move the
 *    element so that baseline lands on the model's — which, surprisingly, does
 *    not depend on the line height at all.
 */

/** Fabric v6's internal text constants (`dist/src/shapes/Text/Text.mjs`). */
const FABRIC_FONT_SIZE_MULT = 1.13;
const FABRIC_BASELINE_FRACTION = 1 - 0.222; // `_renderChars` lifts the baseline by `_fontSizeFraction`

/** Fabric's first baseline, measured from the top of its line box, in px. */
export function fabricFirstBaseline(fontSize: number): number {
  return FABRIC_FONT_SIZE_MULT * FABRIC_BASELINE_FRACTION * fontSize;
}

export interface FabricTextAlignment {
  /** The line height to hand Fabric so its pitch matches the model's. */
  lineHeight: number;
  /**
   * The height to force on the Textbox so its glyphs sit on the model's
   * baselines while its centre stays the model's centre.
   */
  height: number;
}

/**
 * What it takes to make a Fabric Textbox draw on the model's baselines.
 *
 * Feeding Fabric `lineHeight / 1.13` fixes the pitch (its internal multiplier
 * cancels). The baselines need the object's own geometry: Fabric draws line i's
 * baseline at `i × pitch + fabricFirstBaseline` below its box top, and boxes
 * are centred on the object's centre — which is also Fabric's rotation pivot.
 * Forcing the height to `(lines − 1) × modelPitch + 2 × fabricFirstBaseline`
 * puts every baseline on the model's (`lineHeight/2 + i × pitch` below the box
 * top) while keeping the object's centre exactly on the model's, so a rotated
 * scene layer rotates about the same point the model and the export do.
 */
export function fabricTextAlignment(
  fontSize: number,
  lineHeight: number,
  lineCount: number,
): FabricTextAlignment {
  return {
    lineHeight: lineHeight / FABRIC_FONT_SIZE_MULT,
    height: (lineCount - 1) * lineHeight * fontSize + 2 * fabricFirstBaseline(fontSize),
  };
}

export interface FontMetrics {
  ascent: number;
  descent: number;
}

const metricsCache = new Map<string, FontMetrics>();

/**
 * The browser's ascent and descent for a font, measured once and cached by the
 * CSS font shorthand.
 *
 * Uses `fontBoundingBoxAscent/Descent` — the metrics CSS line layout itself
 * uses — so the overlay's shift is measured with the same numbers the browser
 * lays out with. Returns null where the API is missing (old engines, jsdom);
 * callers fall back to no shift rather than a wrong one.
 */
export function fontMetrics(font: string): FontMetrics | null {
  const cached = metricsCache.get(font);
  if (cached) return cached;
  if (typeof document === 'undefined') return null;
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');
  if (!ctx) return null;
  ctx.font = font;
  const measured = ctx.measureText('');
  const { fontBoundingBoxAscent, fontBoundingBoxDescent } = measured;
  if (fontBoundingBoxAscent === undefined || fontBoundingBoxDescent === undefined) return null;
  const metrics = { ascent: fontBoundingBoxAscent, descent: fontBoundingBoxDescent };
  metricsCache.set(font, metrics);
  return metrics;
}

/**
 * Vertical offset (CSS px, at the size the metrics were measured at) that puts
 * a DOM text element's first baseline where the model puts the glyphs'.
 *
 * DOM first baseline = `(lineHeight − ascent − descent) / 2 + ascent`; the
 * model's is `lineHeight / 2`. The line height cancels — the offset is exactly
 * half the difference between a font's descent and its ascent, which is why
 * this is a pure function of the metrics.
 */
export function domBaselineOffset(metrics: FontMetrics): number {
  return (metrics.descent - metrics.ascent) / 2;
}

/** The overlay's font shorthand for a draft, at a CSS pixel size. */
export function draftFont(
  draft: {
    fontFamily: string;
    fontSize: number;
    bold: boolean;
    italic: boolean;
  },
  pixelSize: number,
): string {
  return fontShorthand({
    fontFamily: draft.fontFamily,
    fontSize: pixelSize,
    fontWeight: draft.bold ? 'bold' : 'normal',
    italic: draft.italic,
  });
}
